import { useMemo } from 'react';
import type { Tournament, TournamentStatus } from '@swiss-magic/shared-types';
import {
  useDashboardFilters,
  type DashboardFilterConfig,
  type DashboardFilters,
} from '@/lib/dashboard-filters';

// Story 079 — director dashboard sort & filter.
// Director-specific config for the generic dashboard-filter hook.

const STATUS_CHIPS = ['draft', 'published', 'active', 'completed'] as const;
const TIME_CHIPS = ['past', 'this_month', 'upcoming'] as const;
const SORT_OPTIONS = [
  'default',
  'name_asc',
  'date_asc',
  'date_desc',
  'created_desc',
] as const;

const STATUS_BUCKETS: Record<string, TournamentStatus[]> = {
  draft: ['draft'],
  published: ['published'],
  active: ['registration_open', 'registration_closed', 'in_progress'],
  completed: ['completed'],
};

function statusChipOf(status: TournamentStatus): string {
  for (const chip of STATUS_CHIPS) {
    if (STATUS_BUCKETS[chip].includes(status)) return chip;
  }
  // Future statuses fall through to 'active' so they stay visible
  // rather than getting silently dropped.
  return 'active';
}

function timeChipsOf(startDate: string | null, now: Date): string[] {
  if (!startDate) return [];
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const start = new Date(startDate + 'T00:00:00');
  const chips: string[] = [];
  if (start.getTime() < firstOfMonth.getTime()) chips.push('past');
  if (
    start.getFullYear() === today.getFullYear() &&
    start.getMonth() === today.getMonth()
  ) {
    chips.push('this_month');
  }
  if (start.getTime() > today.getTime()) chips.push('upcoming');
  return chips;
}

function compareDateAsc(a: string | null, b: string | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a.localeCompare(b);
}

function compareDateDesc(a: string | null, b: string | null): number {
  return -compareDateAsc(a, b);
}

const DEFAULT_BUCKET_ORDER: Record<string, number> = {
  active: 0,
  published: 1,
  draft: 2,
  completed: 3,
};

function compareDefault(a: Tournament, b: Tournament): number {
  const ca = statusChipOf(a.status);
  const cb = statusChipOf(b.status);
  if (ca !== cb) return DEFAULT_BUCKET_ORDER[ca] - DEFAULT_BUCKET_ORDER[cb];
  if (ca === 'draft') {
    // Drafts often lack a start date; recently edited is the meaningful order.
    return (b.updatedAt ?? '').localeCompare(a.updatedAt ?? '');
  }
  if (ca === 'completed') {
    return compareDateDesc(a.startDate, b.startDate);
  }
  return compareDateAsc(a.startDate, b.startDate);
}

const COMPARATORS: Record<string, (a: Tournament, b: Tournament) => number> = {
  default: compareDefault,
  name_asc: (a, b) => a.name.localeCompare(b.name),
  date_asc: (a, b) => compareDateAsc(a.startDate, b.startDate),
  date_desc: (a, b) => compareDateDesc(a.startDate, b.startDate),
  created_desc: (a, b) => b.createdAt.localeCompare(a.createdAt),
};

export function useDirectorDashboardFilters(
  tournaments: Tournament[],
): DashboardFilters<Tournament> {
  const config = useMemo<DashboardFilterConfig<Tournament>>(
    () => ({
      statusChips: STATUS_CHIPS,
      timeChips: TIME_CHIPS,
      sortOptions: SORT_OPTIONS,
      classifyStatus: (t) => statusChipOf(t.status),
      classifyTime: (t, now) => timeChipsOf(t.startDate, now),
      matchesSearch: (t, q) => t.name.toLowerCase().includes(q),
      comparators: COMPARATORS,
    }),
    [],
  );
  return useDashboardFilters(tournaments, config);
}
